import axios, { AxiosInstance } from 'axios';
import {
  LoginRequest,
  LoginResponse,
  User,
  Page,
  CreatePageRequest,
  UpdatePageRequest,
  JobOpening,
  CreateJobRequest,
  UpdateJobRequest,
  JobApplication,
  CreateApplicationRequest,
  UpdateApplicationRequest,
  ApplicationStats,
  SiteSetting,
  CreateSettingRequest,
  UpdateSettingRequest
} from '../types/api';

// Configurable API base URL for different deployment scenarios:
// - Development: http://localhost:3001/api
// - Same host (reverse proxy): /api
// - Different hosts: https://api.example.com/api
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add request interceptor to include auth token
    this.api.interceptors.request.use((config) => {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    // Add response interceptor to handle auth errors
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  // Auth endpoints
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    const response = await this.api.post<LoginResponse>('/auth/login', credentials);
    return response.data;
  }

  async getProfile(): Promise<User> {
    const response = await this.api.get<User>('/auth/profile');
    return response.data;
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await this.api.post('/auth/change-password', { currentPassword, newPassword });
  }

  async updateProfile(userData: Partial<User>): Promise<User> {
    const response = await this.api.put<User>('/auth/profile', userData);
    return response.data;
  }

  // User endpoints
  async getUsers(): Promise<User[]> {
    const response = await this.api.get<User[]>('/users');
    return response.data;
  }

  async getUser(id: number): Promise<User> {
    const response = await this.api.get<User>(`/users/${id}`);
    return response.data;
  }

  async createUser(userData: Partial<User>): Promise<User> {
    const response = await this.api.post<User>('/users', userData);
    return response.data;
  }

  async updateUser(id: number, userData: Partial<User>): Promise<User> {
    const response = await this.api.put<User>(`/users/${id}`, userData);
    return response.data;
  }

  async deleteUser(id: number): Promise<void> {
    await this.api.delete(`/users/${id}`);
  }

  // Page endpoints
  async getPages(): Promise<Page[]> {
    const response = await this.api.get<Page[]>('/pages');
    return response.data;
  }

  async getPage(id: number): Promise<Page> {
    const response = await this.api.get<Page>(`/pages/${id}`);
    return response.data;
  }

  async createPage(pageData: CreatePageRequest): Promise<Page> {
    const response = await this.api.post<Page>('/pages', pageData);
    return response.data;
  }

  async updatePage(id: number, pageData: UpdatePageRequest): Promise<Page> {
    const response = await this.api.put<Page>(`/pages/${id}`, pageData);
    return response.data;
  }

  async deletePage(id: number): Promise<void> {
    await this.api.delete(`/pages/${id}`);
  }

  // Job endpoints
  async getJobs(): Promise<JobOpening[]> {
    const response = await this.api.get<JobOpening[]>('/jobs');
    return response.data;
  }

  async getJob(id: number): Promise<JobOpening> {
    const response = await this.api.get<JobOpening>(`/jobs/${id}`);
    return response.data;
  }

  async createJob(jobData: CreateJobRequest): Promise<JobOpening> {
    const response = await this.api.post<JobOpening>('/jobs', jobData);
    return response.data;
  }

  async updateJob(id: number, jobData: UpdateJobRequest): Promise<JobOpening> {
    const response = await this.api.put<JobOpening>(`/jobs/${id}`, jobData);
    return response.data;
  }

  async deleteJob(id: number): Promise<void> {
    await this.api.delete(`/jobs/${id}`);
  }

  // Application endpoints
  async getApplications(): Promise<JobApplication[]> {
    const response = await this.api.get<JobApplication[]>('/applications');
    return response.data;
  }

  async getApplication(id: number): Promise<JobApplication> {
    const response = await this.api.get<JobApplication>(`/applications/${id}`);
    return response.data;
  }

  async createApplication(applicationData: CreateApplicationRequest): Promise<JobApplication> {
    const response = await this.api.post<JobApplication>('/applications', applicationData);
    return response.data;
  }

  async updateApplication(id: number, applicationData: UpdateApplicationRequest): Promise<JobApplication> {
    const response = await this.api.put<JobApplication>(`/applications/${id}`, applicationData);
    return response.data;
  }

  async deleteApplication(id: number): Promise<void> {
    await this.api.delete(`/applications/${id}`);
  }

  async getApplicationStats(): Promise<ApplicationStats> {
    const response = await this.api.get<ApplicationStats>('/applications/stats');
    return response.data;
  }

  // Settings endpoints
  async getSettings(): Promise<SiteSetting[]> {
    const response = await this.api.get<SiteSetting[]>('/settings');
    return response.data;
  }

  async getPublicSettings(): Promise<SiteSetting[]> {
    const response = await this.api.get<SiteSetting[]>('/settings/public');
    return response.data;
  }

  async getSetting(id: number): Promise<SiteSetting> {
    const response = await this.api.get<SiteSetting>(`/settings/${id}`);
    return response.data;
  }

  async getSettingByKey(key: string): Promise<SiteSetting> {
    const response = await this.api.get<SiteSetting>(`/settings/key/${key}`);
    return response.data;
  }

  async createSetting(settingData: CreateSettingRequest): Promise<SiteSetting> {
    const response = await this.api.post<SiteSetting>('/settings', settingData);
    return response.data;
  }

  async updateSetting(id: number, settingData: UpdateSettingRequest): Promise<SiteSetting> {
    const response = await this.api.put<SiteSetting>(`/settings/${id}`, settingData);
    return response.data;
  }

  async updateSettingByKey(key: string, value: string): Promise<SiteSetting> {
    const response = await this.api.put<SiteSetting>(`/settings/key/${key}`, { value });
    return response.data;
  }

  async deleteSetting(id: number): Promise<void> {
    await this.api.delete(`/settings/${id}`);
  }

  async deleteSettingByKey(key: string): Promise<void> {
    await this.api.delete(`/settings/key/${key}`);
  }

  async getSettingsByCategory(category: string): Promise<SiteSetting[]> {
    const response = await this.api.get<SiteSetting[]>(`/settings/category/${category}`);
    return response.data;
  }

  async getSettingsObject(keys: string[]): Promise<Record<string, string>> {
    const response = await this.api.get<Record<string, string>>('/settings/object', {
      params: { keys }
    });
    return response.data;
  }

  // Legacy methods for backward compatibility
  async getAllUsers(): Promise<User[]> {
    return this.getUsers();
  }

  async getUserById(id: number): Promise<User> {
    return this.getUser(id);
  }

  async getAllPages(): Promise<Page[]> {
    return this.getPages();
  }

  async getPageById(id: number): Promise<Page> {
    return this.getPage(id);
  }

  async getPageBySlug(slug: string): Promise<Page> {
    const response = await this.api.get<Page>(`/pages/slug/${slug}`);
    return response.data;
  }

  async getPublishedPages(): Promise<Page[]> {
    const response = await this.api.get<Page[]>('/pages/published');
    return response.data;
  }

  async publishPage(id: number): Promise<Page> {
    const response = await this.api.post<Page>(`/pages/${id}/publish`);
    return response.data;
  }

  async unpublishPage(id: number): Promise<Page> {
    const response = await this.api.post<Page>(`/pages/${id}/unpublish`);
    return response.data;
  }

  async getAllJobs(): Promise<JobOpening[]> {
    return this.getJobs();
  }

  async getJobById(id: number): Promise<JobOpening> {
    return this.getJob(id);
  }

  async getActiveJobs(): Promise<JobOpening[]> {
    const response = await this.api.get<JobOpening[]>('/jobs/active');
    return response.data;
  }

  async activateJob(id: number): Promise<JobOpening> {
    const response = await this.api.post<JobOpening>(`/jobs/${id}/activate`);
    return response.data;
  }

  async deactivateJob(id: number): Promise<JobOpening> {
    const response = await this.api.post<JobOpening>(`/jobs/${id}/deactivate`);
    return response.data;
  }

  async getAllApplications(): Promise<JobApplication[]> {
    return this.getApplications();
  }

  async getApplicationById(id: number): Promise<JobApplication> {
    return this.getApplication(id);
  }

  async submitApplication(applicationData: CreateApplicationRequest): Promise<{ id: number; message: string }> {
    const formData = new FormData();
    formData.append('job_id', applicationData.job_id.toString());
    formData.append('first_name', applicationData.first_name);
    formData.append('last_name', applicationData.last_name);
    formData.append('email', applicationData.email);
    if (applicationData.phone) {
      formData.append('phone', applicationData.phone);
    }
    if (applicationData.motivation_letter) {
      formData.append('motivation_letter', applicationData.motivation_letter);
    }
    formData.append('resume', applicationData.resume);

    const response = await this.api.post<{ id: number; message: string }>('/applications/submit', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async downloadResume(id: number): Promise<Blob> {
    const response = await this.api.get(`/applications/${id}/resume`, {
      responseType: 'blob',
    });
    return response.data;
  }

  async createManualApplication(applicationData: {
    job_id: number;
    first_name: string;
    last_name: string;
    email: string;
    phone?: string;
    motivation_letter?: string;
    status: string;
  }): Promise<JobApplication> {
    const response = await this.api.post<JobApplication>('/applications/manual', applicationData);
    return response.data;
  }
}

const apiService = new ApiService();

export { apiService };
export default apiService;
